import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Skeleton,
  Badge,
} from "@chakra-ui/react";

import React, { useEffect } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

// Custom components
import Card from "components/card/Card";

import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { useSelector } from "react-redux";

import { RootReducer } from "../../../../RootReducer";
import moment from "moment";

interface Invoice {
  status: string;
  amount: number;
  paymentStatus: string;
  processStatus: string;
  rangeFrom: string;
  rangeUntil: string;
}

export default function ColumnsTable(props: {
  columnsData: any;
  tableData: any;
  isLoading: boolean;
  right?: any;
}) {
  const { columnsData, tableData, isLoading } = props;

  const tableInstance = useTable<Invoice>(
    {
      columns: columnsData,
      data: tableData,
      initialState: { pageSize: 200 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const accountInfo = useSelector(
    (state: RootReducer) => state.authentication.accountInfo
  );
  const showPlatformSelectCached = localStorage.getItem("p") === "1";

  let showPlatformSelect = false;
  if (
    (accountInfo && accountInfo.showPlatformSelection) ||
    showPlatformSelectCached
  ) {
    showPlatformSelect = true;
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 200;

  useEffect(() => {
    tableInstance.setPageSize(200);
  }, [tableData, columnsData, isLoading, tableInstance]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  return (
    <Card
      flexDirection="column"
      w="100%"
      flex={1}
      px="0px"
      overflowX={{ sm: "scroll", lg: "scroll" }}
    >
      <Flex px="25px" justify="space-between" mb="10px" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Unpaid & Overdue Invoices
        </Text>
        {props.right}
      </Flex>
      <Table {...getTableProps()} variant="striped" color="gray.500" mb="24px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  pe="10px"
                  key={index}
                  borderColor={borderColor}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize={{ sm: "10px", lg: "12px" }}
                    color="gray.400"
                  >
                    {column.render("Header")}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row, index) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={index}>
                {row.cells.map((cell, index) => {
                  let data;
                  if (cell.column.id === "status") {
                    data = (
                      <Badge
                        colorScheme={
                          cell.value === "OVERDUE"
                            ? "red"
                            : cell.value === "UNPAID"
                            ? "yellow"
                            : "gray"
                        }
                        fontSize="sm"
                        fontWeight="700"
                      >
                        {cell.value}
                      </Badge>
                    );
                  } else if (cell.column.id === "amount") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        ${cell.value?.toFixed(0)}
                      </Text>
                    );
                  } else if (cell.column.id === "paymentStatus") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {cell.value}
                      </Text>
                    );
                  } else if (cell.column.id === "processStatus") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {cell.value}
                      </Text>
                    );
                  } else if (cell.column.id === "period") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {moment(row.original.rangeFrom).format("MM/YYYY")}
                      </Text>
                    );
                  } else {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {cell.value}
                      </Text>
                    );
                  }

                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={index}
                      fontSize={{ sm: "14px" }}
                      maxH="30px !important"
                      py="8px"
                      minW={{ sm: "150px", md: "200px", lg: "2px" }}
                      borderColor="transparent"
                    >
                      <Skeleton
                        borderRadius={16}
                        endColor={"rgba(53,63,255,0.17)"}
                        isLoaded={!isLoading}
                      >
                        {data}
                      </Skeleton>
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Card>
  );
}
